import ApiClientOpen from '../api/axios/apiClientOpen';
import type { BodegaListResponse, BodegaResponse, BodegaRequest } from '../../types';
import qs from 'qs';
import type { StrapiFilters, StrapiSort, StrapiPopulate, StrapiPagination } from '../../types/_strapi.model';

const BODEGA_ENDPOINT = '/bodegas';

interface GetAllBodegasParams {
  filters?: StrapiFilters;
  sort?: StrapiSort | string[];
  fields?: string[];
  populate?: StrapiPopulate | string[];
  pagination?: StrapiPagination;
}

interface GetAllParams {
  page: number;
  limit: number;
  filter?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Service class for Bodega entity operations
 */
class BodegaService {
  /**
   * Get all bodegas with optional filtering, sorting, population, and pagination
   * @param params Object containing filters, sort, fields, populate, and pagination options
   * @returns Promise with list of bodegas and metadata
   */
  async getAllBodegas(params?: GetAllBodegasParams): Promise<BodegaListResponse> {
    try {
      // Build query parameters using qs
      const queryParams = qs.stringify(params || {}, {
        encodeValuesOnly: true, // prettify URL
      });

      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<BodegaListResponse>(`${BODEGA_ENDPOINT}${queryString}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching bodegas:', error);
      throw error;
    }
  }

  /** 
   * Get all bodegas with pagination, filtering, and sorting
   * @param params Object containing pagination, filtering, and sorting options
   * @returns Promise with list of bodegas and metadata
   */
  async getAll(params: GetAllParams): Promise<BodegaListResponse> {
    try {
      const { page, limit, filter, sortBy, sortOrder } = params;
      
      // Build Strapi query parameters
      const strapiParams: any = {
        pagination: {
          page,
          pageSize: limit
        }
      };
      
      // Add filter if provided
      if (filter) {
        strapiParams.filters = {
          $or: [
            { nombre: { $contains: filter } },
            { codigo: { $contains: filter } }
          ]
        }
      }
      
      // Add sorting if provided
      if (sortBy && sortOrder) {
        strapiParams.sort = `${sortBy}:${sortOrder}`;
      }
      
      // Build query parameters using qs
      const queryParams = qs.stringify(strapiParams, {
        encodeValuesOnly: true, // prettify URL
      });

      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<BodegaListResponse>(`${BODEGA_ENDPOINT}${queryString}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching bodegas:', error);
      throw error;
    }
  }

  /**
   * Get a bodega by ID with optional population and field selection
   * @param id Bodega ID
   * @param populate Optional fields to populate
   * @param fields Optional fields to select
   * @returns Promise with bodega data
   */
  async getBodegaById(
    id: number | string, 
    populate?: StrapiPopulate | string[], 
    fields?: string[]
  ): Promise<BodegaResponse> {
    try {
      const queryOptions: any = {};
      
      if (populate) {
        queryOptions.populate = populate;
      }
      
      if (fields) {
        queryOptions.fields = fields;
      }
      
      const queryParams = qs.stringify(queryOptions, {
        encodeValuesOnly: true,
      });
      
      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<BodegaResponse>(`${BODEGA_ENDPOINT}/${id}${queryString}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching bodega with id ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new bodega
   * @param bodegaData Bodega data to create
   * @returns Promise with created bodega data
   */
  async createBodega(bodegaData: BodegaRequest): Promise<BodegaResponse> {
    try {
      const response = await ApiClientOpen.post<BodegaResponse>(BODEGA_ENDPOINT, bodegaData);
      return response.data;
    } catch (error) {
      console.error('Error creating bodega:', error);
      throw error;
    }
  }

  /**
   * Create a new bodega (alias method)
   * @param bodegaData Bodega data to create
   * @returns Promise with created bodega data
   */
  async create(bodegaData: BodegaRequest): Promise<BodegaResponse> {
    return this.createBodega(bodegaData);
  }

  /**
   * Update an existing bodega
   * @param id Bodega ID to update
   * @param bodegaData Updated bodega data
   * @returns Promise with updated bodega data
   */
  async updateBodega(id: number | string, bodegaData: BodegaRequest): Promise<BodegaResponse> {
    try {
      const response = await ApiClientOpen.put<BodegaResponse>(`${BODEGA_ENDPOINT}/${id}`, bodegaData);
      return response.data;
    } catch (error) {
      console.error(`Error updating bodega with id ${id}:`, error);
      throw error;
    }
  }

  /**
   * Update an existing bodega (alias method)
   * @param id Bodega ID to update
   * @param bodegaData Updated bodega data
   * @returns Promise with updated bodega data
   */
  async update(id: number | string, bodegaData: BodegaRequest): Promise<BodegaResponse> {
    return this.updateBodega(id, bodegaData);
  }

  /**
   * Delete a bodega by ID (alias method)
   * @param id Bodega ID to delete
   * @returns Promise indicating success or failure
   */
  async delete(id: number | string): Promise<void> {
    return this.deleteBodega(id);
  }

  /**
   * Delete a bodega by ID
   * @param id Bodega ID to delete
   * @returns Promise indicating success or failure
   */
  async deleteBodega(id: number | string): Promise<void> {
    try {
      await ApiClientOpen.delete(`${BODEGA_ENDPOINT}/${id}`);
    } catch (error) {
      console.error(`Error deleting bodega with id ${id}:`, error);
      throw error;
    }
  }
}

export default new BodegaService();